import axios from "axios";
import _lodash from "lodash";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import Select from "react-select";
import { ScaleLoader } from "react-spinners";
import { CustomLabel } from "shared/components/input/CustomLabel";

export type FormSelectAsyncProps<T extends Record<string, unknown>> = {
  id: string;
  label: string;
  url: string;
  valueKey: keyof T & string;
  valueLabel: keyof T & string;
  value?: T | null;
  visualizacao?: string;
  onChange: (value: T | null, searchTerm?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  parametros?: Record<string, string | number | boolean | undefined>;
  sort?: string;
  noPage?: boolean;
  isLazy?: boolean;
  autoLoadWhenReady?: boolean;
  canLoadOnMenuOpen?: () => boolean;
  nomeAtributoPesquisa?: string;
  className?: string;
  selectContainerStyle?: React.CSSProperties;
  placeholderFontSize?: string;
  formatOptionLabel?: (option: T, meta: { inputValue?: string; context?: string }) => ReactNode;
  filterOption?: (option: { data: T }, input: string) => boolean;
};

export const MENSAGEM_ERRO_CARREGAMENTO = "Não foi possível carregar as opções. Abra a lista para tentar de novo.";

const FormSelectAsync = <T extends Record<string, unknown>>(props: FormSelectAsyncProps<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [searched, setSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [falhouAoCarregar, setFalhouAoCarregar] = useState(false);
  const ultimoTermoBuscadoRef = useRef("");
  // Só a requisição mais recente pode escrever no estado. Trocar o condomínio/bloco ou digitar
  // enquanto a lista anterior ainda carrega dispara uma nova busca; sem isto, a resposta mais
  // lenta (a antiga) chegava por último e sobrescrevia as opções com os filtros errados.
  const requisicaoAtualRef = useRef<AbortController | null>(null);

  const parametrosKey = JSON.stringify(props.parametros || {});

  const loadOptions = useCallback(
    async (input?: string) => {
      const { url, valueLabel, nomeAtributoPesquisa, parametros, noPage, sort } = props;

      // Parâmetros via axios (e não concatenados): o termo digitado vai codificado, então "&",
      // "#" ou "=" no nome buscado não quebram mais a query string.
      const params: Record<string, string | number | boolean> = {
        size: 10000,
        [nomeAtributoPesquisa || valueLabel]: input ?? "",
      };
      if (sort) {
        params.sort = sort;
      }
      if (parametros) {
        Object.entries(parametros).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params[key] = value;
          }
        });
      }

      requisicaoAtualRef.current?.abort();
      const controller = new AbortController();
      requisicaoAtualRef.current = controller;

      setIsLoading(true);
      setFalhouAoCarregar(false);

      try {
        const response = await axios.get(`/api/${url}`, { params, signal: controller.signal });
        if (requisicaoAtualRef.current !== controller) {
          return;
        }
        const result = noPage ? response.data : response.data.content;
        const loaded = Array.isArray(result) ? (result as T[]) : [];
        setOptions(loaded);
        setSearched(true);
      } catch (erro) {
        if (axios.isCancel(erro) || requisicaoAtualRef.current !== controller) {
          return;
        }
        setOptions([]);
        // searched continua falso: abrir a lista de novo tenta outra vez, em vez de mostrar
        // "Nenhum registro foi encontrado" como se a consulta tivesse voltado vazia.
        setSearched(false);
        setFalhouAoCarregar(true);
        toast.error(MENSAGEM_ERRO_CARREGAMENTO, { id: `form-select-async-${props.id}` });
      } finally {
        if (requisicaoAtualRef.current === controller) {
          requisicaoAtualRef.current = null;
          setIsLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parametros compared via parametrosKey
    [props.id, props.url, props.valueLabel, props.nomeAtributoPesquisa, parametrosKey, props.noPage, props.sort]
  );

  useEffect(() => {
    setSearched(false);
    setOptions([]);
    setFalhouAoCarregar(false);

    if (props.autoLoadWhenReady && props.url) {
      loadOptions();
    }
  }, [props.autoLoadWhenReady, props.url, parametrosKey, loadOptions]);

  // Desmontar (ex.: fechar o modal) cancela a busca pendente para ela não escrever em estado morto.
  useEffect(() => () => requisicaoAtualRef.current?.abort(), []);

  const selectedValue = useMemo(() => {
    if (!props.value || !props.value[props.valueKey]) {
      return null;
    }

    if (props.formatOptionLabel) {
      return props.value as T;
    }

    return {
      ...props.value,
      [props.valueLabel]: props.visualizacao || props.value[props.valueLabel],
    } as T;
  }, [props.value, props.visualizacao, props.valueKey, props.valueLabel, props.formatOptionLabel]);

  const customStyles = {
    menuPortal: (provided: object) => ({
      ...provided,
      zIndex: 10060,
    }),
    menu: (provided: object) => ({
      ...provided,
      zIndex: 10060,
    }),
    container: (provided: object) => ({
      ...provided,
      ...(props.selectContainerStyle || {}),
    }),
    control: (provided: object, state: { isFocused: boolean }) => ({
      ...provided,
      borderColor: state.isFocused ? "#83cf9c !important" : "#c4c4c4",
      boxShadow: "none",
      fontSize: "small",
      fontWeight: 200,
    }),
    ...(props.placeholderFontSize && {
      placeholder: (provided: object) => ({
        ...provided,
        fontSize: props.placeholderFontSize,
      }),
    }),
  };

  return (
    <div className={props.className || "col-sm-12"} data-cy={props.id}>
      <CustomLabel>{props.label}</CustomLabel>
      <Select
        inputId={props.id}
        styles={customStyles}
        value={selectedValue}
        options={options}
        isDisabled={props.disabled}
        isClearable
        isLoading={isLoading}
        placeholder={props.placeholder || ""}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        getOptionValue={(option) => String(option[props.valueKey])}
        getOptionLabel={(option) => {
          // getOptionLabel precisa de texto (acessibilidade e filtro padrão do react-select).
          // formatOptionLabel pode devolver JSX, e String(<span/>) virava "[object Object]".
          const rotulo = props.formatOptionLabel?.(option, {
            inputValue: ultimoTermoBuscadoRef.current,
            context: "value",
          });
          return typeof rotulo === "string" ? rotulo : String(option[props.valueLabel] ?? "");
        }}
        formatOptionLabel={props.formatOptionLabel}
        filterOption={props.filterOption as never}
        onChange={(event) => {
          props.onChange((event as T | null) ?? null, ultimoTermoBuscadoRef.current);
        }}
        onMenuOpen={() => {
          const canLoadOnMenuOpen = !props.canLoadOnMenuOpen || props.canLoadOnMenuOpen();
          if (props.url && (props.isLazy || !_lodash.isEmpty(props.parametros)) && !searched && !isLoading && canLoadOnMenuOpen) {
            loadOptions();
          }
        }}
        onInputChange={(newValue, { action }) => {
          if (action !== "input-change") {
            return newValue;
          }

          ultimoTermoBuscadoRef.current = newValue;
          return newValue;
        }}
        loadingMessage={() => (
          <span style={{ display: "block", textAlign: "center", padding: "8px" }}>
            <ScaleLoader height="10px" color="#83cf9c" />
          </span>
        )}
        noOptionsMessage={() =>
          falhouAoCarregar ? MENSAGEM_ERRO_CARREGAMENTO : "Nenhum registro foi encontrado"
        }
      />
    </div>
  );
};

export default FormSelectAsync;
